/**
 * The pricing engine. `ARCHITECTURE.md` §Principles 7: "the pricing engine is a
 * pure function with golden tests." The single source of truth
 * (`.docs/00-index.md` §5): "no arithmetic on catalog amounts in the API or the
 * SPA".
 *
 * `PRICING.md` §Formula, verbatim:
 *
 *   monthly  = round10( Σ block_amount × blocks × (1 − bundle_discount(n_blocks)) × tier )
 *              + Σ custom_line
 *   extra    = round5 ( extra_unit × tier ) × qty
 *   project  = round10( (template_amount + Σ add_ons) × tier )
 *
 * A custom line (`PRICING.md` §Custom lines) is the one number in this file that is
 * **not** computed: it is the amount one client agreed for work the rate card does
 * not sell as a block, already in their billing currency, and it is added after the
 * rounding exactly as given. It earns no bundle discount, takes no tier, is not
 * converted and does not count as a block for the discount on the others.
 *
 * Every input is a plain number or a plain object — no database types, no dates, no
 * I/O. The rate card is passed in: the catalog owns the tables, this file owns the
 * arithmetic. Each function returns the ordered steps as well as the total, because
 * `client/billing.html` and `studio/client-detail.html` both show the working, and
 * a total nobody can explain is a support ticket.
 */
import { comparePeriods, firstFullMonth, periodOf, periodsBetween, } from './dates.js';
import { DomainError } from './errors.js';
import { add, applyBps, convert, money, multiplyByQty, BPS_PER_WHOLE, round10, round5, subtract, sum, } from './money.js';
/* ------------------------------------------------------------------ market tiers */
export const TIER_CODES = [1, 2, 3];
const TIER_MULTIPLIERS = [1, 0.7, 0.5];
/**
 * The multiplier as basis points. Written out rather than computed: `0.7 * 10000`
 * is `7000.000000000001` in binary floating point, and `applyBps` would refuse it.
 *
 * Exported because a breakdown carries the multiplier and the wire carries the basis
 * points — `catalog/service.ts` is the one caller, and it having its own switch
 * would be a second table of three numbers.
 */
export function tierBps(multiplier) {
    switch (multiplier) {
        case 1:
            return 10_000;
        case 0.7:
            return 7_000;
        case 0.5:
            return 5_000;
    }
}
/**
 * Whether a tier actually changed the price.
 *
 * Tier 1 is ×1.0, so its row in a build-up adds nothing to the column it is in —
 * which is why the three tables that print one all had to ask this, and all three
 * asked it by writing `tier.multiplier_bps !== BPS_PER_WHOLE` out again. What each of
 * them *does* with the answer is genuinely different and is each mockup's own:
 * `client/billing.html` states the tier in a `kv` beside the table and leaves the row
 * out, `studio/client-detail.html` prints the row with an em dash, and the studio's
 * price builder prints the amount. The predicate is the shared half.
 */
export function isTiered(tier) {
    return tier.multiplier_bps !== BPS_PER_WHOLE;
}
/**
 * The inverse: basis points as they are stored, back to the multiplier.
 *
 * `market_tier.multiplier` is a `multiplier_bps` integer column, because a `real`
 * column would hand back `0.7000000000000001` and `applyBps` refuses that — the same
 * reason `tierBps` above is a switch rather than a multiplication. This is the one
 * place a stored tier becomes a `Tier`, so the catalog restates neither number.
 */
export function tierMultiplierOf(bps) {
    switch (bps) {
        case 10_000:
            return 1;
        case 7_000:
            return 0.7;
        case 5_000:
            return 0.5;
        default:
            throw new DomainError('ValidationFailed', 'A tier multiplier is 10000, 7000 or 5000 basis points (PRICING.md, Market tiers).', { bps: String(bps) });
    }
}
function assertTier(tier) {
    if (!TIER_CODES.includes(tier.code)) {
        throw new DomainError('ValidationFailed', 'A market tier is 1, 2 or 3.', {
            code: String(tier.code),
        });
    }
    if (!TIER_MULTIPLIERS.includes(tier.multiplier)) {
        throw new DomainError('ValidationFailed', 'A tier multiplier is 1, 0.7 or 0.5.', {
            multiplier: String(tier.multiplier),
        });
    }
}
/** The deepest band whose `minBlocks` the package reaches. No band reached is 0 bps. */
function bundleDiscountBps(blockCount, discounts) {
    let best = null;
    for (const band of discounts) {
        if (!Number.isSafeInteger(band.minBlocks) || band.minBlocks < 1) {
            throw new DomainError('ValidationFailed', 'A bundle discount band starts at one block.', {
                minBlocks: String(band.minBlocks),
            });
        }
        if (!Number.isSafeInteger(band.bps) || band.bps < 0 || band.bps > 10_000) {
            throw new DomainError('ValidationFailed', 'A bundle discount is 0–10,000 basis points.', {
                bps: String(band.bps),
            });
        }
        if (band.minBlocks <= blockCount && (best === null || band.minBlocks > best.minBlocks)) {
            best = { minBlocks: band.minBlocks, bps: band.bps };
        }
    }
    return best?.bps ?? 0;
}
/**
 * The monthly amount for a package of blocks.
 *
 * Blocks are summed at list in USD, the bundle discount comes off, the market tier
 * is applied, the result is converted if the client bills in EUR, and `round10`
 * runs **once**, at the end, in the billing currency (`.docs/00-index.md` §6).
 */
export function monthly(items, tier, discounts, currency, fx, customLines = []) {
    assertTier(tier);
    if (items.length === 0 && customLines.length === 0) {
        throw new DomainError('InvalidPackage', 'A package is at least one block or one custom line (PRICING.md).', {});
    }
    const custom = customLinesOf(customLines, currency);
    const services = new Set();
    const families = new Set();
    const lines = [];
    let blockCount = 0;
    for (const item of items) {
        if (!Number.isSafeInteger(item.blocks) || item.blocks < 1) {
            throw new DomainError('InvalidPackage', 'A package item is at least one whole block.', {
                serviceCode: item.serviceCode,
                blocks: String(item.blocks),
            });
        }
        if (services.has(item.serviceCode)) {
            throw new DomainError('InvalidPackage', `"${item.serviceCode}" is in the package twice — use blocks, not a second item.`, { serviceCode: item.serviceCode });
        }
        services.add(item.serviceCode);
        const family = item.replacesFamily ?? null;
        if (family !== null) {
            if (families.has(family)) {
                throw new DomainError('InvalidPackage', `"${item.serviceCode}" replaces another block already in the package; they do not stack (PRICING.md).`, { serviceCode: item.serviceCode, family });
            }
            families.add(family);
        }
        blockCount += item.blocks;
        lines.push(multiplyByQty(money(item.blockAmountUsd, 'USD'), item.blocks));
    }
    // With no blocks at all — a package that is custom lines only — every step below
    // is a zero in its own currency, and the rounding line is zero too.
    const list = sum(lines, 'USD');
    const bps = bundleDiscountBps(blockCount, discounts);
    const bundleDiscount = applyBps(list, -bps);
    const afterDiscount = add(list, bundleDiscount);
    const tierAmount = applyBps(afterDiscount, tierBps(tier.multiplier));
    const converted = currency === 'USD' ? null : convert(tierAmount, fx.usdPerEur, currency);
    const exact = converted ?? tierAmount;
    const blocksTotal = round10(exact);
    const customTotal = sum(custom.map((line) => line.amount), currency);
    return {
        currency,
        blockCount,
        list,
        bundleDiscountBps: bps,
        bundleDiscount,
        afterDiscount,
        tier: { code: tier.code, multiplier: tier.multiplier, amount: tierAmount },
        converted,
        rounding: subtract(blocksTotal, exact),
        blocksTotal,
        customLines: custom,
        customTotal,
        total: add(blocksTotal, customTotal),
    };
}
/**
 * The custom lines, checked and echoed.
 *
 * Four refusals, each by name so the line can be found on a thirty-line package:
 * a blank name, a name used twice (case-insensitively — "Website" and "website" on
 * one invoice would read as a typo), an amount in a currency that is not the
 * billing currency (nothing here converts it, on purpose), and an amount that is
 * not a positive whole number of minor units. A zero line is a line somebody forgot
 * to price, and a negative one is a discount, which is an invoice line and not a
 * package line.
 */
function customLinesOf(lines, currency) {
    const seen = new Set();
    return lines.map((line) => {
        const name = line.name.trim();
        if (name.length === 0) {
            throw new DomainError('InvalidPackage', 'A custom line has a name; it is what the invoice reads.', {});
        }
        const key = name.toLowerCase();
        if (seen.has(key)) {
            throw new DomainError('InvalidPackage', `"${name}" is on the package twice. A custom line is named once; put the two amounts ` +
                'together.', { name });
        }
        seen.add(key);
        if (line.amount.currency !== currency) {
            throw new DomainError('InvalidPackage', `"${name}" is in ${line.amount.currency} and the client bills in ${currency}. A custom ` +
                'line is agreed in the billing currency; nothing converts it.', { name, currency: line.amount.currency, billing_currency: currency });
        }
        if (!Number.isSafeInteger(line.amount.amount) || line.amount.amount <= 0) {
            throw new DomainError('InvalidPackage', `"${name}" is ${String(line.amount.amount)}; a custom line is a positive whole number ` +
                'of minor units. A credit is a discount line on an invoice, not a package line.', { name, amount: String(line.amount.amount) });
        }
        return { name, amount: money(line.amount.amount, currency) };
    });
}
/* ------------------------------------------------------------- the rate card */
/**
 * Which line of the formula an amount is rounded by (`PRICING.md` §Formula):
 * `monthly` and `project` round to $10, an extra’s unit rounds to $5.
 */
export const PRICE_KINDS = ['monthly', 'extra', 'project'];
/**
 * One Tier 1 USD catalog amount, as a client at `tier` billing in `currency` sees it.
 *
 * `.docs/10-catalog.md` §DRY watchlist: "`displayPrice` (tier + fx + round) is one
 * function in `packages/shared/pricing.ts`; the client catalogue route and 11–12’s
 * snapshots both call it." A rate card that rounded differently from the invoice
 * would be a page a client could hold up against a charge, so there is one function
 * and it applies the same three steps in the same order as the three formulas above:
 * tier, then the billing currency, then one rounding at the end.
 *
 * This is a **list price for one line**, not a package total. A package rounds once
 * over the whole sum (`monthly` above); rounding each block here and adding them up
 * would not reach the same number, which is exactly why `monthly` exists separately.
 */
export function displayPrice(amountUsd, tier, currency, fx, kind) {
    assertTier(tier);
    const tiered = applyBps(money(amountUsd, 'USD'), tierBps(tier.multiplier));
    const inCurrency = currency === 'USD' ? tiered : convert(tiered, fx.usdPerEur, currency);
    return kind === 'extra' ? round5(inCurrency) : round10(inCurrency);
}
/**
 * A price already agreed in a client's own currency, read as its USD equivalent.
 *
 * The other direction from `displayPrice`: that one takes a raw Tier 1 USD catalog
 * amount and tiers, converts and rounds it into what a client sees. This takes an
 * amount that has *already* been tiered, converted and rounded once — a retainer's
 * `monthly_amount`, a EUR invoice line — and only does the currency step again, at
 * `round10`, which is `.docs/17-studio-read-models.md` §formulae "MRR at month end"'s
 * own rounding. Re-applying the tier here would double it: Vela's €890 is already
 * Tier 2's 0.7×, and `session 10`'s own worked example is `$970.20 ÷ 1.09 → €890`,
 * so the USD figure the studio's MRR sums is `round10(€890 × 1.09) = $970`.
 */
export function usdEquivalent(amount, fx) {
    return amount.currency === 'USD' ? amount : round10(convert(amount, fx.usdPerEur, 'USD'));
}
/**
 * The same conversion **to the cent** — no `round10`.
 *
 * `usdEquivalent` above rounds to $10 because it answers "what is this *price* in
 * USD", and a price on a rate card is a round number. This one answers "what is this
 * *amount* in USD" for a figure that is owed or divided rather than printed: a
 * past-due remainder, a §Paid numerator, a value-delivered total.
 * `readmodels/formulae.md` §Converting to USD is the rule and names which is which.
 *
 * The two came apart on `studio/money.html`: the past-due hero converted exactly and
 * the ageing chart beside it converted as a price, so the same two invoices read
 * $2,460.20 and $2,460.00 and twenty cents had nowhere to be. Three read models had
 * each written their own private copy of this function (`toUsd`, `toUsdRaw`,
 * `toUsdAmount`) before it existed here.
 */
export function usdExact(amount, fx) {
    return amount.currency === 'USD' ? amount : convert(amount, fx.usdPerEur, 'USD');
}
/* ----------------------------------------------------------------------- extras */
/**
 * The unit price of an extra, rounded to $5.
 *
 * Note the order in `PRICING.md`: `round5(extra_unit × tier) × qty`. The **unit** is
 * rounded, then multiplied — so `Tier 3 article = round5($87.50) = $90`, and four of
 * them are $360, not $350. Invoice lines need this on its own: `invoice_line` has a
 * `CHECK (amount = qty * unit_amount)` (`ARCHITECTURE.md` §Invariants).
 *
 * One line over `displayPrice`, so "what an extra costs" and "what the rate card
 * prints beside that block" cannot come apart.
 */
export function extraUnit(unitAmountUsd, tier, currency, fx) {
    return displayPrice(unitAmountUsd, tier, currency, fx, 'extra');
}
/** `round5(extra_unit × tier) × qty`, the whole line. */
export function extra(unitAmountUsd, qty, tier, currency, fx) {
    if (!Number.isFinite(qty) || qty < 0) {
        throw new DomainError('ValidationFailed', 'A quantity is zero or more.', { qty: String(qty) });
    }
    return multiplyByQty(extraUnit(unitAmountUsd, tier, currency, fx), qty);
}
/** `PRICING.md` §Projects: "Project and retainer signed together: 10% off the project." */
export const RETAINER_PROJECT_DISCOUNT_BPS = 1000;
export function project(templateAmountUsd, addOns, tier, options, currency, fx) {
    assertTier(tier);
    const template = money(templateAmountUsd, 'USD');
    const resolved = addOns.map((addOn) => {
        if (addOn.kind === 'amount') {
            if (!Number.isSafeInteger(addOn.qty) || addOn.qty < 1) {
                throw new DomainError('ValidationFailed', 'An add-on is bought a whole number of times.', {
                    code: addOn.code,
                    qty: String(addOn.qty),
                });
            }
            return {
                code: addOn.code,
                amount: multiplyByQty(money(addOn.amountUsd, 'USD'), addOn.qty),
            };
        }
        if (!Number.isSafeInteger(addOn.bps) || addOn.bps < 0) {
            throw new DomainError('ValidationFailed', 'A percentage add-on is zero or more bps.', {
                code: addOn.code,
                bps: String(addOn.bps),
            });
        }
        return { code: addOn.code, amount: applyBps(template, addOn.bps) };
    });
    const subtotal = sum([template, ...resolved.map((line) => line.amount)], 'USD');
    const tierAmount = applyBps(subtotal, tierBps(tier.multiplier));
    const retainerDiscountBps = options.retainerClient ? RETAINER_PROJECT_DISCOUNT_BPS : 0;
    const retainerDiscount = applyBps(tierAmount, -retainerDiscountBps);
    const afterDiscount = add(tierAmount, retainerDiscount);
    const converted = currency === 'USD' ? null : convert(afterDiscount, fx.usdPerEur, currency);
    const exact = converted ?? afterDiscount;
    const total = round10(exact);
    return {
        currency,
        template,
        addOns: resolved,
        subtotal,
        tier: { code: tier.code, multiplier: tier.multiplier, amount: tierAmount },
        retainerDiscountBps,
        retainerDiscount,
        afterDiscount,
        converted,
        rounding: subtract(total, exact),
        total,
    };
}
/* ------------------------------------------------------- allowances and credits */
/** `PRICING.md` §Content types. The credit cost of each comes from the catalog. */
export const CONTENT_TYPE_CODES = ['reel', 'carousel', 'static', 'story'];
/**
 * What each content type is **called**, singular and plural.
 *
 * A property of the enum rather than of a page: three screens name these codes —
 * the dashboard's allowance foot, the request ledger and the report's social
 * calendar — and each had written its own. One of them pluralised by appending an
 * `s`, which makes `story` into "storys"; the golden world records zero stories on
 * every allowance, so the only reason nobody has seen it is that nobody has posted
 * one. Pass it to `formatQty`, which picks the form from the number.
 */
export const CONTENT_TYPE_NOUNS = {
    reel: { one: 'reel', other: 'reels' },
    carousel: { one: 'carousel', other: 'carousels' },
    static: { one: 'static', other: 'statics' },
    story: { one: 'story', other: 'stories' },
};
/**
 * Credits a set of pieces costs. `PLAN-2026-09` is 8 reels, 4 carousels, 4 statics
 * and 2 stories, which is 62 credits against a 60-credit Base allowance.
 */
export function contentCredits(pieces, creditTable) {
    const known = CONTENT_TYPE_CODES;
    for (const code of Object.keys(pieces)) {
        if (!known.includes(code)) {
            // A typo here would silently cost zero credits, which is a money bug.
            throw new DomainError('ValidationFailed', `"${code}" is not a content type.`, { code });
        }
    }
    let credits = 0;
    for (const code of CONTENT_TYPE_CODES) {
        const count = pieces[code] ?? 0;
        if (!Number.isSafeInteger(count) || count < 0) {
            throw new DomainError('ValidationFailed', 'A piece count is a whole number, zero or more.', {
                code,
                count: String(count),
            });
        }
        const perPiece = creditTable[code];
        if (!Number.isSafeInteger(perPiece) || perPiece < 1) {
            throw new DomainError('ValidationFailed', 'Every content type costs at least one credit.', {
                code,
                credits: String(perPiece),
            });
        }
        credits += count * perPiece;
    }
    return credits;
}
/**
 * How many decimal places a quantity may carry. `work_order_line.qty` and
 * `deliverable.qty` are both `numeric(12, 3)`, so anything finer is a number the
 * database would silently shorten.
 */
const QTY_DECIMALS = 3;
/**
 * Rounds a quantity to `QTY_DECIMALS`, without `Math` — see the file header rule.
 *
 * Exported because **adding two valid quantities does not give one**: `6 + 0.1 + 0.2`
 * is `6.300000000000001` in IEEE doubles, and `allowanceExcess` refuses anything
 * finer than three decimals. Anywhere a caller sums quantities before asking about an
 * allowance — `retainers.recordDeliverable` does, once — the sum goes through here
 * first, or a legitimate half-hour is refused with a message about the caller's input.
 */
export function roundQuantity(value) {
    if (!Number.isFinite(value)) {
        throw new DomainError('ValidationFailed', 'A quantity is a finite number.', {
            value: String(value),
        });
    }
    return Number(value.toFixed(QTY_DECIMALS));
}
const toQtyScale = roundQuantity;
/**
 * `SERVICES.md` §Retainers: "Allowances reset each period and do not roll over.
 * Work past an allowance is an extra."
 *
 * `used` may be **fractional**, because one allowance in `PRICING.md` is: Odoo
 * management is sold in hours and `client/report-2026-08.html` prints `6.0 / 6`
 * against tickets of 2.0, 1.5, 1.0, 1.0 and 0.5. `included` is always whole — every
 * allowance on the card is a count of articles, credits, edits, channels, platforms
 * or hours — and so is the number of decimals a quantity may carry, because
 * `deliverable.qty` is `numeric(12, 3)`.
 *
 * `over` is rounded back to that same scale: `7.5 − 6` is exact, but a sum of three
 * fractional quantities is not, and `0.30000000000000004` of an hour would reach an
 * invoice line whose `CHECK (amount = round(qty × unit_amount))` was computed from a
 * different number.
 */
export function allowanceExcess(used, included) {
    if (!Number.isFinite(used) || used < 0 || toQtyScale(used) !== used) {
        throw new DomainError('ValidationFailed', 'Usage is zero or more, with at most three decimal places.', { used: String(used) });
    }
    if (!Number.isSafeInteger(included) || included < 0) {
        throw new DomainError('ValidationFailed', 'An allowance is a whole number, zero or more.', {
            included: String(included),
        });
    }
    return { used, included, over: used > included ? toQtyScale(used - included) : 0 };
}
/**
 * How many **whole pieces** of one content type fall past a credit allowance.
 *
 * A content block's allowance is a credit total and its overage price is per piece
 * (`PRICING.md` §Content types: reel $50, carousel $30), so the two are in different
 * units and something has to convert. `client/report-2026-08.html` says which way:
 * "Credits come off the allowance in the order things are published" — August's 60
 * credits covered twelve reels exactly, and the two reels after them were billed
 * whole.
 *
 * So a piece that straddles the boundary is charged: you cannot publish six tenths
 * of a reel, and the alternative — billing a fraction of one — would put a quantity
 * on an invoice line that nothing in the world corresponds to.
 *
 * `usedCredits` is what the period had already spent before these pieces.
 */
export function contentPiecesOverAllowance(pieces, creditsPerPiece, usedCredits, includedCredits) {
    for (const [name, value] of [
        ['pieces', pieces],
        ['creditsPerPiece', creditsPerPiece],
        ['usedCredits', usedCredits],
        ['includedCredits', includedCredits],
    ]) {
        if (!Number.isSafeInteger(value) || value < 0) {
            throw new DomainError('ValidationFailed', `${name} is a whole number, zero or more.`, {
                [name]: String(value),
            });
        }
    }
    if (creditsPerPiece < 1) {
        throw new DomainError('ValidationFailed', 'Every content type costs at least one credit.', {
            creditsPerPiece: String(creditsPerPiece),
        });
    }
    const spent = usedCredits + pieces * creditsPerPiece;
    if (spent <= includedCredits)
        return 0;
    const overCredits = spent - includedCredits;
    // Integer ceiling, written out: `Math.ceil` is lint-banned outside `money.ts` and
    // `dates.ts` (`.docs/00-index.md` §5), and this is a count rather than an amount.
    const remainder = overCredits % creditsPerPiece;
    const whole = (overCredits - remainder) / creditsPerPiece;
    const over = remainder === 0 ? whole : whole + 1;
    return over > pieces ? pieces : over;
}
/**
 * Billable usage for a postpay widget: `clamp(qty, included, cap) − included`
 * (`.docs/00-index.md` §6). Events past the cap are recorded and never billed —
 * `SERVICES.md` §Postpay: "Past the cap the widget returns a `limit_reached` state."
 */
export function usageBillable(qty, included, cap) {
    for (const [name, value] of [
        ['qty', qty],
        ['included', included],
        ['cap', cap],
    ]) {
        if (!Number.isSafeInteger(value) || value < 0) {
            throw new DomainError('ValidationFailed', `${name} is a whole number, zero or more.`, {
                [name]: String(value),
            });
        }
    }
    if (cap < included) {
        throw new DomainError('ValidationFailed', 'A cap is at least the included allowance.', {
            included,
            cap,
        });
    }
    const capped = qty > cap ? cap : qty;
    return (capped < included ? included : capped) - included;
}
/**
 * The most a widget can bill in one month, at a given cap.
 *
 * `client/settings.html` §Widget caps prints it beside the cap box, and the API answers
 * it on every widget (`WidgetSchema.worst_case`) — which is the whole reason a page
 * does not compute it. It is here rather than in either caller because both had written
 * it: `modules/usage/service.ts` `worstCase` to answer with, and the settings page to
 * preview a cap the reader has typed and the API has not seen. Two copies of "cap minus
 * included, at the unit price" is a copy of a money rule.
 *
 * A cap below the included allowance is `usageBillable`'s refusal, not a zero.
 */
export function worstCaseAtCap(unitAmount, includedQty, cap) {
    return multiplyByQty(unitAmount, usageBillable(cap, includedQty, cap));
}
/**
 * What one use of a widget costs a client, in their billing currency.
 *
 * **Not tiered and not rounded**, and both halves are decisions with a document
 * behind them. `client/billing.html` §5: "Widget unit prices are not on the
 * published rate card — each type is scoped, and the unit price is agreed when it
 * goes live and then snapshotted onto every event." An agreed price is not a rate
 * card price, so the market tier has nothing to multiply — which is the reading
 * session 10 already took when it left `widget_type.unit_amount` off the tier
 * (`seed/catalog.ts`).
 *
 * And the rounding rules cannot apply here at all: `displayPrice` rounds a `monthly`
 * or `project` amount to $10 and an `extra` unit to $5, and a conversation costs
 * **$0.12**. Either rule would round it to zero and every widget in the system would
 * be free. So the only step left is the client's currency, at the internal rate, to
 * the cent — which is `convert`, and this is one line over it so that "what a use
 * costs" has one implementation rather than a `currency === 'USD'` ternary in the
 * three places that need it.
 *
 * (`.docs/13-usage-platform.md` §1 says "via `displayPrice`". It is the session file
 * that is wrong, per `.docs/00-index.md` §1, and the report says so.)
 */
export function usageUnitPrice(unitAmountUsd, currency, fx) {
    const usd = money(unitAmountUsd, 'USD');
    return currency === 'USD' ? usd : convert(usd, fx.usdPerEur, currency);
}
/* ------------------------------------------------------------ widget fees (46c) */
/**
 * Whether a widget charges **per unit** at all — the one question every reader of a
 * widget's allowance, cap or billable count asks first (session 46c).
 *
 * A widget type has two prices, a unit price and a monthly fee, and three shapes
 * follow from them: **metered** (a unit price, perhaps a fee beside it), **flat** (a
 * fee and no unit price — the blog, at $5.00 a month) and **free** (neither — the
 * forms). Only the first is measured against an allowance and a cap, so a widget this
 * answers `false` for has neither (`widget_unmetered_has_no_allowance` is the
 * database's half), is never `limit_reached`, is never handed to `usageBillable`, and
 * no page offers it a cap. One predicate rather than `unit_amount > 0` at every one of
 * those, because a reader that asked it differently — `>= 1`, `!== 0`, "has a cap" —
 * would be a reader that disagrees about a free widget.
 *
 * Takes the unit price in minor units, in whatever currency the caller holds it: a
 * conversion never turns a price into zero (`convert` rounds half up, and the smallest
 * card price is a cent), so the answer is the same before and after `usageUnitPrice`.
 */
export function isMetered(unitAmount) {
    if (!Number.isSafeInteger(unitAmount) || unitAmount < 0) {
        throw new DomainError('ValidationFailed', 'A unit price is a whole number of minor units, zero or more.', { unit_amount: String(unitAmount) });
    }
    return unitAmount > 0;
}
/**
 * The refusal of an allowance or a cap on something that is not charged per unit.
 *
 * Four doors draw this one line — a type created or changed with a default allowance or
 * cap and no unit price, a widget created with one, and `widgets_set_cap` and
 * `widgets_set_included` on such a widget — and a sentence written four times would be
 * four sentences by the second edit. `Unprocessable` with one entry per field, because
 * each field is something to put back to zero and a form shows each under its own box.
 * The database refuses the same rows (`*_unmetered_has_no_allowance`); this is what a
 * caller reads instead of a 500.
 */
export function notMeteredRefusal(subject, fields, meta = {}) {
    return new DomainError('Unprocessable', `A ${subject} with no unit price is not charged per unit, so it has no allowance and no ` +
        'cap: those bound what is billed per use, and nothing is.', meta, fields.map((path) => ({
        path,
        code: 'not_metered',
        message: `Always 0 for a ${subject} that is not charged per unit — there is nothing per unit to ` +
            'include or to cap.',
    })));
}
/**
 * What a widget's monthly fee is, in its client's billing currency.
 *
 * **The unit price's own conversion, named for what it prices**: untiered and
 * unrounded, converted once to the cent at the internal rate, and snapshotted onto the
 * widget at creation like the unit price beside it. `usageUnitPrice` says why neither
 * the tier nor `round10` belongs here — an agreed price is not a card price, and a
 * five-dollar fee rounded to ten would be a different fee. A sibling rather than a
 * second call of `usageUnitPrice` at the creation site, so that what is being priced
 * is readable at the call, and so that a fee that ever does need a rule of its own has
 * one place to get it.
 */
export function widgetMonthlyFee(monthlyAmountUsd, currency, fx) {
    return usageUnitPrice(monthlyAmountUsd, currency, fx);
}
/**
 * The first month a widget owes its monthly fee for.
 *
 * The owner's rule (2026-10-21): **a widget's first, partial month is free; the fee
 * starts with its first full calendar month**. A month is full when the widget was live
 * on its 1st — `live_since <= ` the first day of it — which is `dates.firstFullMonth`,
 * the retainer's own "first month billed in full", so the two rules cannot come apart.
 * There is no proration anywhere else: a widget pays for a whole month or not at all.
 *
 * **And never a month before the one the portal created it in**, read in UTC like every
 * usage period (`dates.periodOf`). `live_since` is a day somebody types, and a blog the
 * studio records in October as live since January did not have the portal hosting it in
 * February; without this bound its first run would bill nine months at once. The month
 * of creation itself is owed when `live_since` says it was a full one.
 *
 * **Nor a month before its fee came back** (session 46d). A widget whose fee was nothing
 * for a while — free on the card, or under a free client price — owes nothing for that
 * while, and no run closed those months because there was nothing to bill; without this
 * bound, the first run after a fee came back would bill every one of them. The month it
 * came back in is owed, because a client price applies to the whole month in progress.
 */
export function widgetFeeStartsIn(life) {
    const firstFull = firstFullMonth(life.liveSince);
    const created = periodOf(life.createdAt);
    const floor = comparePeriods(firstFull, created) >= 0 ? firstFull : created;
    const since = life.feeSince ?? null;
    return since !== null && comparePeriods(since, floor) > 0 ? since : floor;
}
/**
 * Every month a widget owes its fee for, from the first (`widgetFeeStartsIn`) up to and
 * including `through`, in order. Empty when `through` comes first.
 *
 * Whether a fee is due at all (`monthly_amount > 0`), whether the widget was paused
 * when the month closed, and which months are already closed are the billing run's
 * facts, not arithmetic, so they are the caller's: this answers only which months are
 * months of the widget's life.
 */
export function widgetFeeMonths(life, through) {
    const start = widgetFeeStartsIn(life);
    return comparePeriods(start, through) > 0 ? [] : periodsBetween(start, through);
}
/**
 * How many units a widget is on course for by the end of the month.
 *
 * `qty / daysElapsed × daysInPeriod`, to the nearest ten
 * (`.docs/13-usage-platform.md` §4). The goldens are both widget pages at day 24 of
 * 31: `1,264 → ≈ 1,630` and `389 → ≈ 500`.
 *
 * **To the nearest ten, and that is the point.** A projection is an estimate and the
 * pages print it with a `≈`; `1,632.67` would read as a measurement. Ten is also
 * what makes the projected charge come out at the figures the pages publish —
 * `(1,630 − 1,000) × $0.12 = $75.60` and `(500 − 300) × $0.40 = $80.00` — because
 * the charge is computed from the rounded projection rather than beside it.
 *
 * Written without `Math.round`, which is lint-banned outside `money.ts` and
 * `dates.ts` and would be the wrong tool anyway: this is a count of conversations,
 * not an amount.
 */
export function usageProjection(qty, daysElapsed, daysInPeriod) {
    for (const [name, value] of [
        ['qty', qty],
        ['daysElapsed', daysElapsed],
        ['daysInPeriod', daysInPeriod],
    ]) {
        if (!Number.isSafeInteger(value) || value < 0) {
            throw new DomainError('ValidationFailed', `${name} is a whole number, zero or more.`, {
                [name]: String(value),
            });
        }
    }
    if (daysElapsed < 1 || daysElapsed > daysInPeriod) {
        throw new DomainError('ValidationFailed', 'The days elapsed fall inside the period, and a period at least a day old.', { daysElapsed, daysInPeriod });
    }
    // A month that is over is not a projection: the count is the answer, and dividing
    // by the whole month and multiplying by it again would round a real figure.
    if (daysElapsed === daysInPeriod)
        return qty;
    const projected = (qty * daysInPeriod) / daysElapsed;
    // Half up to the nearest ten, in arithmetic rather than through `Math`: add five,
    // then take the remainder off. Both operands are non-negative, so `%` is a floor.
    const shifted = projected + PROJECTION_UNIT / 2;
    const rounded = shifted - (shifted % PROJECTION_UNIT);
    /*
     * Never below what has already happened.
     *
     * Rounding to ten is right for the figures the widget pages print — 1,632.67 is not
     * a measurement — and wrong for a widget with three conversations on it, which
     * projects to 3.32 and rounds to **zero**. A page that says "3 recorded, projected
     * ≈ 0" is arithmetic nobody can defend, and the charge beside it would say $0.00
     * against a cost that has already been incurred. A month cannot end with fewer
     * units than it already has, so that is the floor.
     */
    return rounded < qty ? qty : rounded;
}
/** The unit a projection is rounded to. Ten uses — see `usageProjection`. */
export const PROJECTION_UNIT = 10;
//# sourceMappingURL=pricing.js.map